import React from 'react'
import { useTranslation } from 'react-i18next'
import { ModernInput, ModernButton } from '../ui'
import { formatCep } from '../../utils/brFormat'

export type BrazilAddressValue = {
  zipCode: string
  street: string
  number: string
  complement: string
  neighborhood: string
  city: string
  state: string
}

type BrazilAddressFieldErrors = Partial<Record<keyof BrazilAddressValue, boolean>>

type BrazilAddressFieldsProps = {
  value: BrazilAddressValue
  onChange: (next: BrazilAddressValue) => void
  onZipBlur?: () => void
  onZipSearchPress?: () => void
  isLoadingZip?: boolean
  zipError?: string | null
  disabled?: boolean
  lockDerivedAddressFields?: boolean
  fieldErrors?: BrazilAddressFieldErrors
  i18nPrefix?: string
}

export const BrazilAddressFields: React.FC<BrazilAddressFieldsProps> = ({
  value,
  onChange,
  onZipBlur,
  onZipSearchPress,
  isLoadingZip,
  zipError,
  disabled,
  lockDerivedAddressFields,
  fieldErrors,
  i18nPrefix = 'customers.address',
}) => {
  const { t } = useTranslation()
  const p = (sub: string) => t(`${i18nPrefix}.${sub}`)
  const derivedLocked = Boolean(lockDerivedAddressFields) && !disabled

  const set = (patch: Partial<BrazilAddressValue>) => {
    onChange({ ...value, ...patch })
  }

  const handleSearchClick = () => {
    const run = onZipSearchPress ?? onZipBlur
    void run?.()
  }

  const zipCodeDigits = value.zipCode.replace(/\D/g, '')
  const isZipCodeValid = zipCodeDigits.length === 8

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
        <ModernInput
          label={p('zipCode')}
          required
          value={value.zipCode}
          disabled={disabled}
          onChange={e => set({ zipCode: formatCep(e.target.value) })}
          onBlur={() => onZipBlur?.()}
          placeholder="00000-000"
          error={zipError || undefined}
          invalid={Boolean(fieldErrors?.zipCode || zipError)}
        />
        <ModernButton
          type="button"
          variant="outline"
          className="h-10 w-full shrink-0 sm:mt-6 sm:w-auto sm:min-w-[8.5rem]"
          disabled={disabled || isLoadingZip || !isZipCodeValid}
          onClick={handleSearchClick}
        >
          {isLoadingZip ? 'Buscando...' : p('lookupZip')}
        </ModernButton>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2">
          <ModernInput
            label={p('street')}
            required
            value={value.street}
            disabled={disabled || derivedLocked}
            onChange={e => set({ street: e.target.value })}
            placeholder=""
            invalid={Boolean(fieldErrors?.street)}
          />
        </div>
        <ModernInput
          label={p('number')}
          required
          value={value.number}
          disabled={disabled}
          onChange={e => set({ number: e.target.value })}
          placeholder="123"
          invalid={Boolean(fieldErrors?.number)}
        />
      </div>
      <ModernInput
        label={p('complement')}
        value={value.complement}
        disabled={disabled}
        onChange={e => set({ complement: e.target.value })}
        placeholder=""
      />
      <ModernInput
        label={p('neighborhood')}
        required
        value={value.neighborhood}
        disabled={disabled || derivedLocked}
        onChange={e => set({ neighborhood: e.target.value })}
        placeholder=""
        invalid={Boolean(fieldErrors?.neighborhood)}
      />
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <div className="min-w-0 flex-1">
          <ModernInput
            label={p('city')}
            required
            value={value.city}
            disabled={disabled || derivedLocked}
            onChange={e => set({ city: e.target.value })}
            placeholder=""
            invalid={Boolean(fieldErrors?.city)}
          />
        </div>
        <div className="w-full shrink-0 sm:w-[5.25rem]">
          <ModernInput
            label={p('state')}
            required
            value={value.state}
            disabled={disabled || derivedLocked}
            onChange={e =>
              set({ state: e.target.value.replace(/\D/g, '').slice(0, 2).toUpperCase() })
            }
            placeholder="UF"
            maxLength={2}
            className="text-center uppercase tracking-widest"
            invalid={Boolean(fieldErrors?.state)}
          />
        </div>
      </div>
      {isLoadingZip && (
        <p className="text-xs text-muted-foreground">{p('searchingZip')}</p>
      )}
      {zipError && <p className="text-sm text-destructive">{zipError}</p>}
    </div>
  )
}
